import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import "react-loading-skeleton/dist/skeleton.css";
import { useFetchPageNameFromURL } from "../../../../../react-query/block-detail/useFetchPageNameFromURL";
import { useEffect, useState, useRef } from "react";
import LinkAddDialogHeader from "./Header";
import LinkAddDialogFooter from "./Footer";
import LinkAddDialogMain from "./Main";
import { isValidUrl } from "../../../../../utils/common/block/links/isValidUrl";

import { LinkClientData } from "../../../../../views/TaskDetail/Link/type";

interface LinkAddDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initData: LinkClientData | null;
  onAdd: ({ url, pageName }: { url: string; pageName: string }) => void;
  onEdit: ({ index, url, pageName }: { index: number; url: string; pageName: string }) => void;
}

export const LinkAddDialog: React.FC<LinkAddDialogProps> = ({
  open,
  onOpenChange,
  initData,
  onAdd,
  onEdit,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [linkNameInput, setLinkNameInput] = useState(initData?.name ?? "");
  const [isLinkNameModified, setIsLinkNameModified] = useState(false);
  const [isUrlModified, setIsUrlModified] = useState(false);
  const [isEditMode, setIsEditMode] = useState(initData !== null);

  const [debouncedValue, setDebouncedValue] = useState(initData?.url ?? "");

  const hasInput = React.useMemo(() => debouncedValue?.length > 0, [debouncedValue?.length]);
  const showBottomUI = isEditMode || hasInput;

  const {
    data: fetchedLinkName,
    isFetching,
    isSuccess,
  } = useFetchPageNameFromURL({
    url: debouncedValue,
    // NOTE: Fetch 조건을 명확하게 분리
    // 1. 새로 추가할 때: debouncedValue가 있으면 fetch
    // 2. 수정 모드일 때: URL이 변경된 경우에만 fetch
    isUrlDirty:
      debouncedValue !== "" &&
      (!isEditMode || // 새로 추가하는 경우
        (isEditMode && isUrlModified)), // 수정 모드에서 URL이 변경된 경우
  });

  // NOTE 모달이 열릴 때 상태 초기화
  useEffect(() => {
    if (open) {
      setIsUrlModified(false);
      // 수정 모드일 때는 사용자가 이미 입력한 linkName을 보호하기 위해 isLinkNameModified를 true로 설정
      setIsLinkNameModified(initData !== null);
      setLinkNameInput(initData?.name ?? "");
      setIsEditMode(initData !== null);
    }
  }, [open, initData]);

  // NOTE ESC 키 전역 이벤트 리스너 및 외부 클릭 처리
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        event.preventDefault();
        event.stopPropagation();
        handleModalClose();
      }
    };

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        handleModalClose();
      }
    };

    // 캡처 단계에서 이벤트를 가로채기
    document.addEventListener("keydown", handleGlobalKeyDown, true);
    document.addEventListener("mousedown", handleClickOutside, true);

    return () => {
      document.removeEventListener("keydown", handleGlobalKeyDown, true);
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [open]);

  // NOTE: fetch된 페이지 이름을 linkNameInput에 설정
  // 조건: 1) fetch 성공 2) 사용자가 linkName을 수정하지 않음
  // 3) fetchedLinkName이 빈 문자열인 경우 debouncedValue를 fallback으로 사용
  useEffect(() => {
    if (isSuccess && !isLinkNameModified) {
      const newLinkName = fetchedLinkName || debouncedValue;
      setLinkNameInput(newLinkName);
    }
  }, [isSuccess, isLinkNameModified, fetchedLinkName, debouncedValue]);

  const handleLinkNameInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLinkNameInput(e.target.value);
    setIsLinkNameModified(true);
  };

  const clearModalState = () => {
    setLinkNameInput("");
    setIsLinkNameModified(false);
    setIsUrlModified(false);
    setIsEditMode(false);
  };

  const handleModalClose = () => {
    clearModalState();
    onOpenChange(false);
  };

  const handleModalSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isValidUrl(debouncedValue)) {
      return;
    }

    // linkNameInput이 비어있거나 공백만 있을 때 URL을 name으로 사용
    const finalName = linkNameInput.trim() || debouncedValue;

    if (isEditMode && initData?.index !== undefined) {
      onEdit({
        index: initData.index,
        url: debouncedValue,
        pageName: finalName,
      });
    } else {
      onAdd({
        url: debouncedValue,
        pageName: finalName,
      });
    }

    clearModalState();
    onOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // NOTE enter의 경우 form 내부 발생으로 이벤트 전파만 막으면 잘 처리됨.
    // 단, ESC의 경우 radix 라이브러리 내부 이벤트로 처리되어 처리 불가능함.
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (e.nativeEvent.isComposing) return;

      handleModalSubmit(e as unknown as React.FormEvent<HTMLFormElement>);
    }

    // backspace
    if (e.key === "Backspace") {
      e.stopPropagation();
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={onOpenChange}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[1000] bg-black/40 transition-opacity duration-200 data-[state=open]:animate-moba-fade-in data-[state=closed]:opacity-0" />
        <Dialog.Content
          ref={containerRef}
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
          // NOTE 모달 내부 버튼 클릭 시 다른 이벤트 핸들러의 outside로 전파되는 것을 방지
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onKeyDown={handleKeyDown}
          className={`fixed left-1/2 top-1/2 z-[1001] flex w-[350px] -translate-x-1/2 -translate-y-1/2 flex-col rounded-[6px] border border-[#3E4042] bg-[#313435] shadow-2xl data-[state=closed]:animate-center-modal-scale-out data-[state=open]:animate-center-modal-scale-in`}
        >
          <LinkAddDialogHeader onClose={handleModalClose} />

          <form
            className="flex w-full flex-col"
            onSubmit={handleModalSubmit}
          >
            <LinkAddDialogMain
              linkNameInput={linkNameInput}
              debouncedValue={debouncedValue}
              initData={initData}
              handleLinkNameInputChange={handleLinkNameInputChange}
              showBottomUI={showBottomUI}
              isFetching={isFetching}
              setDebouncedValue={setDebouncedValue}
              isUrlModified={isUrlModified}
              setIsUrlModified={setIsUrlModified}
              setIsLinkNameModified={setIsLinkNameModified}
            />

            <LinkAddDialogFooter
              showBottomUI={showBottomUI}
              onCancel={handleModalClose}
              isEditMode={isEditMode}
              isValidValue={isValidUrl(debouncedValue)}
            />
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
